import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "hooks";
import { Role } from "types/Role";
import PendingApprovalsContainer from "containers/pending-approvals-container/pending-approvals-container";
import MedicalCheckQueue from "containers/medical-check-queue/medical-check-queue";
import MyTrainingPanel from "containers/my-training-container/my-training-panel";
import InvoiceApprovalsQueue from "containers/invoice-approvals-queue/invoice-approvals-queue";
import { canAccessMedicalQueue } from "utils/onboardingPermissions";
import { canSeeInvoiceInbox } from "features/invoice-approvals/invoice-approvals.slice";
import WsApprovalsQueue from "features/work-security-approvals/WsApprovalsQueue";
import { canSeeWsApprovalsFor } from "features/work-security-approvals/work-security-approvals.slice";
import ProbationReviewsQueue from "features/probation-reviews/ProbationReviewsQueue";
import {
  canSeeProbationReviewsTab,
  ProbationReviewsState,
} from "features/probation-reviews/probation-reviews.slice";
import QueueFrame from "features/cabinet-tasks/queue-frame";
import SignQueuePage from "features/cabinet-queues/sign-queue-page";
import ReviewQueuePage from "features/cabinet-queues/review-queue-page";
import ExpiringQueuePage from "features/cabinet-queues/expiring-queue-page";

/** What a queue's `allowed` rule may look at. */
interface Viewer {
  role: Role;
  companyId: string;
  /** What the probation queue last answered (only the probation row looks at it). */
  probation: ProbationReviewsState;
}

interface QueuePage {
  /** The address part after /my-work/. */
  segment: string;
  titleKey: string;
  element: (companyId: string) => React.ReactNode;
  /** Who may open it; left out = everyone. An unauthorised person goes back to «Užduotys». */
  allowed?: (viewer: Viewer) => boolean;
}

// The queue pages, each at /app/:companyId/my-work/<segment>. How it is read: the
// first row whose segment matches the address is shown inside the common frame
// (a way back and the title); an unknown segment, or a row whose `allowed` rule
// says no, sends the person to «Užduotys». The existing component of each queue
// loads its own data.
const QUEUE_PAGES: QueuePage[] = [
  { segment: "pending-signature", titleKey: "tasks_queue_sign_work", element: () => <SignQueuePage /> },
  { segment: "pending-files", titleKey: "tasks_queue_review", element: () => <ReviewQueuePage /> },
  {
    segment: "pending-approvals",
    titleKey: "tasks_queue_customer_finance",
    element: () => <PendingApprovalsContainer />,
  },
  {
    segment: "invoice-approvals",
    titleKey: "tasks_queue_invoices",
    element: () => <InvoiceApprovalsQueue />,
    allowed: ({ role }) => canSeeInvoiceInbox(role.permissionsgroups),
  },
  {
    segment: "ws-approvals",
    titleKey: "tasks_queue_ws",
    element: () => <WsApprovalsQueue />,
    allowed: ({ role, companyId }) => canSeeWsApprovalsFor(role, companyId),
  },
  {
    segment: "probation-reviews",
    titleKey: "tasks_queue_probation",
    element: () => <ProbationReviewsQueue />,
    // Open while the person's queue has something in it, or always for HR reach. A refresh
    // on this page opens it before the queue has answered, so until this company's queue
    // has loaded the page stays (its component asks for the queue).
    allowed: ({ role, companyId, probation }) => {
      const isAnswered = probation.companyId === companyId && probation.loaded;
      return !isAnswered || canSeeProbationReviewsTab(probation.count, role);
    },
  },
  { segment: "expiring-documents", titleKey: "tasks_group_expiring", element: () => <ExpiringQueuePage /> },
  {
    segment: "medical-checks",
    titleKey: "tasks_queue_medical",
    element: (companyId) => <MedicalCheckQueue companyId={companyId} />,
    allowed: ({ role }) => canAccessMedicalQueue(role),
  },
  { segment: "training", titleKey: "tasks_queue_training", element: () => <MyTrainingPanel /> },
];

function QueuePages() {
  const { t } = useTranslation();
  const { companyId = "", "*": rest = "" } = useParams();
  const role = useAppSelector((state) => state.authReducer.role);
  const probation = useAppSelector((state) => state.probationReviewsReducer);

  const tasksPath = `/app/${companyId}/my-work`;
  const page = QUEUE_PAGES.find(({ segment }) => segment === rest.split("/")[0]);
  if (!page) return <Navigate to={tasksPath} replace />;

  if (page.allowed) {
    // The tasks guard draws nothing until the role is this company's, so here it is.
    if (!role) return null;

    const viewer: Viewer = { role, companyId, probation };
    if (!page.allowed(viewer)) return <Navigate to={tasksPath} replace />;
  }

  return (
    <QueueFrame companyId={companyId} title={t(page.titleKey)}>
      {page.element(companyId)}
    </QueueFrame>
  );
}

export default QueuePages;
